/**
 * Data hook for chat user operations (add/remove users)
 * Separates GraphQL operations from UI components
 */

import type {
  ChatUserOperationInput,
  EditChatConfig,
  ChatDetailsResponse,
  ChatType,
} from '@choco-admin/types';
import { useCallback, useState, useRef, useEffect } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  useAddChatUsersMutation,
  useRemoveChatUsersMutation,
  AddChatUsersMutation,
  RemoveChatUsersMutation,
} from '../../../generated';

// Helper function to transform mutation response to ChatDetailsResponse
const transformChatResponse = (
  chatData:
    | NonNullable<AddChatUsersMutation['chatInviteUsers']>
    | NonNullable<RemoveChatUsersMutation['chatRemoveUsers']>
): ChatDetailsResponse => {
  return {
    id: chatData.id,
    users:
      chatData.users.edges?.map((edge) => ({
        id: edge.node.id,
        name: edge.node.name || '',
        email: edge.node.email || undefined,
        phone: edge.node.phone,
        supplier: edge.node.supplier,
        orderPreferences: {
          whatsApp: edge.node.orderPreference?.whatsApp !== null,
          sms: edge.node.orderPreference?.sms !== null,
          fax: edge.node.orderPreference?.fax !== null,
        },
      })) || [],
    // Other fields will be populated by the refetch query
    chatName: '',
    restaurantName: '',
    address: '',
    customerNumber: '',
    isEnabled: true,
    status: 'active',
    type: 'ORDERING' as ChatType,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    additionalEmails: [],
    // Entity references
    buyerEntity: null,
    supplierEntity: null,
    // User lists
    buyerUsers: [],
    supplierUsers: [],
  };
};

export interface UseChatUserOperationsResponse {
  addUsers: (input: ChatUserOperationInput[]) => Promise<void>;
  removeUsers: (input: ChatUserOperationInput[]) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

export const useChatUserOperations = (
  config: EditChatConfig = {}
): UseChatUserOperationsResponse => {
  const { onSuccess, onError, captureException } = config;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { addNotification } = useNotification();

  // Use refs to store callback functions to avoid dependency issues
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const captureExceptionRef = useRef(captureException);

  // Update refs when callbacks change
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    captureExceptionRef.current = captureException;
  }, [onSuccess, onError, captureException]);

  const [addChatUsersMutation] = useAddChatUsersMutation();
  const [removeChatUsersMutation] = useRemoveChatUsersMutation();

  const addUsers = useCallback(
    async (input: ChatUserOperationInput[]): Promise<void> => {
      if (input.length === 0) return;

      setIsLoading(true);
      setError(null);

      try {
        // Group by chatId for batch operations
        const operationsByChat = input.reduce(
          (acc, operation) => {
            if (!acc[operation.chatId]) {
              acc[operation.chatId] = [];
            }
            acc[operation.chatId].push(operation.userId);
            return acc;
          },
          {} as Record<string, string[]>
        );

        // Execute all add operations
        const promises = Object.entries(operationsByChat).map(
          ([chatId, userIds]) =>
            addChatUsersMutation({
              variables: {
                id: chatId,
                userIds,
                first: 30,
                withAllUserFields: true,
              },
              refetchQueries: ['GetChatData'],
              awaitRefetchQueries: true,
            })
        );

        const results = await Promise.all(promises);

        addNotification({
          title: 'Users Added',
          message: `Successfully added ${input.length} user(s) to the chat.`,
          type: 'success',
        });

        // Get the updated chat data from the last mutation
        const chatData = results[results.length - 1]?.data?.chatInviteUsers;
        if (chatData) {
          onSuccessRef.current?.(transformChatResponse(chatData));
        }
      } catch (err) {
        const addError = err as Error;
        setError(addError);

        addNotification({
          title: 'Failed to Add Users',
          message: addError.message || 'An error occurred while adding users.',
          type: 'error',
        });

        onErrorRef.current?.(addError);
        captureExceptionRef.current?.(addError);
        throw addError;
      } finally {
        setIsLoading(false);
      }
    },
    [addChatUsersMutation, addNotification]
  );

  const removeUsers = useCallback(
    async (input: ChatUserOperationInput[]): Promise<void> => {
      if (input.length === 0) return;

      setIsLoading(true);
      setError(null);

      try {
        // Group by chatId for batch operations
        const operationsByChat = input.reduce(
          (acc, operation) => {
            if (!acc[operation.chatId]) {
              acc[operation.chatId] = [];
            }
            acc[operation.chatId].push(operation.userId);
            return acc;
          },
          {} as Record<string, string[]>
        );

        // Execute all remove operations
        const promises = Object.entries(operationsByChat).map(
          ([chatId, userIds]) =>
            removeChatUsersMutation({
              variables: {
                id: chatId,
                userIds,
                first: 30,
                withAllUserFields: true,
              },
              refetchQueries: ['GetChatData'],
              awaitRefetchQueries: true,
            })
        );

        const results = await Promise.all(promises);

        addNotification({
          title: 'Users Removed',
          message: `Successfully removed ${input.length} user(s) from the chat.`,
          type: 'success',
        });

        // Get the updated chat data from the last mutation
        const chatData = results[results.length - 1]?.data?.chatRemoveUsers;
        if (chatData) {
          onSuccessRef.current?.(transformChatResponse(chatData));
        }
      } catch (err) {
        const removeError = err as Error;
        setError(removeError);

        addNotification({
          title: 'Failed to Remove Users',
          message:
            removeError.message || 'An error occurred while removing users.',
          type: 'error',
        });

        onErrorRef.current?.(removeError);
        captureExceptionRef.current?.(removeError);
        throw removeError;
      } finally {
        setIsLoading(false);
      }
    },
    [removeChatUsersMutation, addNotification]
  );

  return {
    addUsers,
    removeUsers,
    isLoading,
    error,
  };
};
